import { useState, useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { FiX, FiArrowRight, FiArrowLeft, FiCheck, FiPlus, FiSettings, FiZap } from 'react-icons/fi';
import { BsRocket, BsLightbulb, BsDatabase } from 'react-icons/bs';
import { useGlobal } from '../Context/GlobalContext';
import { useLanguage } from '../Context/LanguageContext';
import './WorkspaceTrainingTour.css';
import OutlinedSecondaryButton from './Buttons/OutlinedSecondaryButton';
import LinkButton from './Buttons/LinkButton';
import PrimaryButton from './Buttons/PrimaryButton';

const WorkspaceTrainingTour = ({ show, onClose, onComplete }) => {
  const { t } = useLanguage();
  const { contentDomain } = useGlobal();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Construct GIF URLs with fallback
  const gifUrls = useMemo(() => {
    const domain = contentDomain || 'https://content.brandvoxai.com';
    return {
      addContent: `${domain}/assets/images/training-gifs/add-content.gif`,
      manageAndTraining: `${domain}/assets/images/training-gifs/manage-and-train.gif`
    };
  }, [contentDomain]);

  useEffect(() => {
    if (!show) return;

    // Cleanup on unmount or when modal closes
    return () => {
      const highlightedElements = document.querySelectorAll('.tour-highlight-pulse');
      highlightedElements.forEach(el => el.classList.remove('tour-highlight-pulse'));
    };
  }, [show]);

  const slides = [
    {
      title: t('components.workspacetrainingtour.welcomeTitle'),
      description: t('components.workspacetrainingtour.welcomeDescription'),
      imageAlt: 'Welcome illustration',
      noImage: true,
      compact: true,
      icon: BsDatabase
    },
    {
      title: t('components.workspacetrainingtour.step1Title'),
      description: t('components.workspacetrainingtour.step1Description'),
      gifType: 'addContent',
      imageAlt: 'Adding content to workspace',
      highlight: t('components.workspacetrainingtour.step1Highlight'),
      icon: FiPlus
    },
    {
      title: t('components.workspacetrainingtour.step2Title'),
      description: t('components.workspacetrainingtour.step2Description'),
      gifType: 'manageAndTraining',
      imageAlt: 'Managing training content',
      highlight: t('components.workspacetrainingtour.step2Highlight'),
      icon: FiSettings
    },
    {
      title: t('components.workspacetrainingtour.step3Title'),
      description: t('components.workspacetrainingtour.step3Description'),
      gifType: 'manageAndTraining',
      imageAlt: 'Training the workspace',
      highlight: t('components.workspacetrainingtour.step3Highlight'),
      icon: FiZap
    },
    {
      title: t('components.workspacetrainingtour.finalTitle'),
      description: t('components.workspacetrainingtour.finalDescription'),
      imageAlt: 'Setup complete',
      cta: true,
      noImage: true,
      compact: true,
      icon: BsRocket,
      showDocsLink: true
    }
  ];

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const handleFinish = () => {
    onComplete();
  };

  const handleDotClick = (index) => {
    setCurrentSlide(index);
  };

  const currentSlideData = slides[currentSlide];
  const isLastSlide = currentSlide === slides.length - 1;
  const isFirstSlide = currentSlide === 0;

  return (
    <>
      <Modal
        show={show}
        onHide={handleSkip}
        centered
        size="lg"
        className="workspace-training-tour-modal"
        backdrop={true}
        backdropClassName="tour-custom-backdrop"
      >
        <Modal.Header className="border-0 pb-0">
          <button
            className="tour-close-btn"
            onClick={handleSkip}
            aria-label={t('components.workspacetrainingtour.closeTour')}
          >
            <FiX size={20} />
          </button>
        </Modal.Header>

        <Modal.Body className="pt-2">
          <div className={`tour-slide-container ${currentSlideData.compact ? 'compact' : ''}`}>
            {/* Slide content */}
            <div className="tour-slide-content">
              <div className="tour-slide-title-container">
                {currentSlideData.icon && (
                  <currentSlideData.icon className="tour-slide-icon" />
                )}
                <h3 className="workspace-training-tour-modal tour-slide-title">{currentSlideData.title}</h3>
              </div>
              <p className="tour-slide-description">{currentSlideData.description}</p>

              {currentSlideData.highlight && (
                <div className="tour-slide-highlight">
                  <BsLightbulb className="highlight-icon" />
                  <span>{currentSlideData.highlight}</span>
                </div>
              )}

              {/* Image/GIF - render both GIFs but show/hide to prevent restart */}
              {!currentSlideData.noImage && currentSlideData.gifType && (
                <div className="tour-slide-image-container">
                  <img
                    src={gifUrls.addContent}
                    alt="Adding content to workspace"
                    className="tour-slide-image"
                    style={{ display: currentSlideData.gifType === 'addContent' ? 'block' : 'none' }}
                  />
                  <img
                    src={gifUrls.manageAndTraining}
                    alt="Managing and training workspace"
                    className="tour-slide-image"
                    style={{ display: currentSlideData.gifType === 'manageAndTraining' ? 'block' : 'none' }}
                  />
                </div>
              )}

              {/* Documentation Link for final slide */}
              {currentSlideData.showDocsLink && (
                <div style={{
                  marginTop: '20px',
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <p style={{ marginBottom: '12px', fontSize: '14px', color: '#6c757d' }}>
                    {t('components.workspacetrainingtour.learnMoreText')}
                  </p>
                  <a
                    href="https://brandvoxai.com/docs/training"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '10px 20px',
                      backgroundColor: 'var(--primary)',
                      color: 'white',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '14px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = 'var(--primary-hover)'}
                    onMouseOut={(e) => e.target.style.backgroundColor = 'var(--primary)'}
                  >
                    {t('components.workspacetrainingtour.viewFullDocumentation')} →
                  </a>
                </div>
              )}
            </div>

            {/* Progress dots */}
            <div className="tour-progress-dots">
              {slides.map((_, index) => (
                <button
                  key={index}
                  className={`tour-dot ${index === currentSlide ? 'active' : ''} ${index < currentSlide ? 'completed' : ''}`}
                  onClick={() => handleDotClick(index)}
                  aria-label={t('components.workspacetrainingtour.goToSlide', { number: index + 1 })}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <div className="tour-navigation">
              <div className="tour-nav-left">
                {!isFirstSlide && (
                  <OutlinedSecondaryButton
                    onClick={handlePrev}
                    mobileWidth="100%"
                  >
                    <FiArrowLeft className="me-2" />
                    {t('components.workspacetrainingtour.previous')}
                  </OutlinedSecondaryButton>
                )}
              </div>

              <div className="tour-nav-right">
                <LinkButton
                  className="tour-skip-btn"
                  onClick={handleSkip}
                  mobileWidth="100%"
                >
                  {t('components.workspacetrainingtour.skipTour')}
                </LinkButton>

                {isLastSlide ? (
                  <PrimaryButton
                    mobileWidth="100%"
                    onClick={handleFinish}
                  >
                    <FiCheck className="me-2" />
                    {t('components.workspacetrainingtour.getStarted')}
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    mobileWidth="100%"
                    onClick={handleNext}
                  >
                    {t('components.workspacetrainingtour.next')}
                    <FiArrowRight className="ms-2" />
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default WorkspaceTrainingTour;
